import type { InferOutput } from 'valibot'
import { array, boolean, literal, nullish, number, object, pipe, string, transform } from 'valibot'
import { baseCatalogueSchema } from '~/utils/crystallize/catalogue'
import {
  components,
  image,
  numericData,
  relatedDocumentsData,
  relatedProductVariantsData,
  richTextData,
  singleImage,
  singleLineData,
} from '~/utils/crystallize/component-schemas'
import { crystallizeFolderSchema } from '~/utils/crystallize/folder'
import { contentBlockPiece, quotePiece, templatePiece } from '~/utils/crystallize/pieces'

/**
 * Similarly to {@link transformComponents}, we convert an array of price variants to an object of prices with camelCased keys.
 *
 * @param variants
 */
export function transformPriceVariants(variants: { identifier: string, price: number }[]) {
  return variants.reduce((acc, { identifier, price }) => ({
    ...acc,
    [toCamelCase(identifier)]: price,
  }), {})
}

const priceVariants = pipe(
  array(
    object({
      identifier: string(),
      price: number(),
    }),
  ),
  transform(transformPriceVariants),
  object({
    default: number(),
  }),
)

const productVariantComponents = object({
  title: nullish(singleLineData),
  subtitle: nullish(singleLineData),
  description: nullish(richTextData),
  serie: nullish(singleLineData),
  componenttype: nullish(singleLineData),
  effectiveVatPercent: nullish(numericData),
  thema1: nullish(singleLineData),
  thema2: nullish(singleLineData),
  thema3: nullish(singleLineData),
  thema4: nullish(singleLineData),
})

export const productVariant = object({
  name: string(),
  sku: string(),
  isDefault: boolean(),
  components: components(productVariantComponents),
  price: number(),
  priceVariants,
  firstImage: nullish(image),
  subscriptionPlans: nullish(
    array(
      object({
        identifier: string(),
        periods: array(
          object({
            id: string(),
            initial: nullish(object({ priceVariants })),
            recurring: nullish(object({ priceVariants })),
          }),
        ),
      }),
    ),
  ),
})

const productComponents = object({
  title: nullish(singleLineData),
  // It's a requirement from Core that this has a norwegian name...
  tittel: nullish(singleLineData),
  subtitle: nullish(singleLineData),
  illustration: singleImage,
  quote: nullish(quotePiece),
  legalCommentary: nullish(contentBlockPiece),
  journals: nullish(contentBlockPiece),
  templateMetadata: nullish(contentBlockPiece),
  templates: nullish(
    array(
      object({
        template: nullish(templatePiece),
      }),
    ),
  ),
  relatedProducts: nullish(relatedDocumentsData),
  includedText: nullish(richTextData),
  includedList: nullish(
    array(
      object({
        text: nullish(singleLineData),
      }),
    ),
  ),
  literature: nullish(
    array(
      object({
        metadata: nullish(contentBlockPiece),
        items: nullish(relatedDocumentsData),
      }),
    ),
  ),
})

export const productSchema = object({
  ...baseCatalogueSchema.entries,
  type: literal('product'),
  vatType: object({
    name: string(),
    percent: number(),
  }),
  variants: array(productVariant),
  components: components(productComponents),
})

export const productsSchema = crystallizeFolderSchema(
  'produktkatalog',
  productSchema,
  object({
    title: nullish(singleLineData),
    description: nullish(richTextData),
    highlighted: nullish(relatedProductVariantsData),
    productsTitle: nullish(singleLineData),
    productsDescription: nullish(richTextData),
  }),
)

export function groupProducts(catalogue: InferOutput<typeof productsSchema> | null) {
  const products: CrystallizeProduct[] = []
  let highlighted: CrystallizeProduct | undefined

  const children = catalogue?.children ?? []

  for (const product of children) {
    if (product.variants.length === 0) {
      continue
    }

    const isHighlighted = catalogue?.components.highlighted?.some(it => product.variants.some(v => v.sku === it.sku))

    if (isHighlighted && !highlighted) {
      highlighted = product
    }
    else {
      if (isHighlighted) {
        console.error('Multiple highlighted products found, should be disallowed by min/max rule in Crystallize product shape.')
      }

      products.push(product)
    }
  }

  return {
    ...catalogue,
    highlighted,
    products,
  }
}

export type ProductCatalogue = ReturnType<typeof groupProducts>
